import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, ArrowLeft, Play, Pause, RotateCcw, Clock, Check,
  Users, Calendar, Trash2, Activity, AlertCircle, TrendingUp,
  Target, Brain, Zap, BookOpen,
} from 'lucide-react';
import { TRAINING_PLANS } from './trainingPlans';

const API = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('prokids_token');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  if (res.status === 204) return null;
  return res.json();
}

const PLAN_ICONS = { tecnica: Target, tactica: Brain, fisico: Zap, mental: BookOpen };
const PLAN_COLORS = {
  tecnica: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  tactica: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  fisico:  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  mental:  { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
};

const STATUS_STYLES = {
  DRAFT:       { label: 'Borrador',    color: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'En curso',    color: 'bg-blue-100 text-blue-700' },
  COMPLETED:   { label: 'Completada',  color: 'bg-emerald-100 text-emerald-700' },
  CANCELLED:   { label: 'Cancelada',   color: 'bg-red-100 text-red-700' },
};

const MEASUREMENT_TYPES = [
  { key: 'SPRINT_20M',       label: 'Sprint 20m',           unit: 's',     group: 'Físico' },
  { key: 'AGILITY_505',      label: 'Agilidad 505',         unit: 's',     group: 'Físico' },
  { key: 'VERTICAL_JUMP',    label: 'Salto vertical',       unit: 'cm',    group: 'Físico' },
  { key: 'FLEXIBILITY',      label: 'Flexibilidad',         unit: 'cm',    group: 'Físico' },
  { key: 'BALL_CONTROL_30S', label: 'Dominio balón 30s',    unit: 'reps',  group: 'Técnico' },
  { key: 'PASS_ACCURACY',    label: 'Precisión pase /10',   unit: 'hits',  group: 'Técnico' },
  { key: 'SLALOM_DRIBBLE',   label: 'Slalom conducción',    unit: 's',     group: 'Técnico' },
  { key: 'HEIGHT',           label: 'Altura',               unit: 'cm',    group: 'Antropo.' },
  { key: 'WEIGHT',           label: 'Peso',                 unit: 'kg',    group: 'Antropo.' },
  { key: 'CONCENTRATION',    label: 'Concentración 1-5',    unit: 'score', group: 'Mental' },
  { key: 'TEAMWORK',         label: 'Trabajo en equipo 1-5', unit: 'score', group: 'Mental' },
  { key: 'LEADERSHIP',       label: 'Liderazgo 1-5',        unit: 'score', group: 'Mental' },
];

// ─── Session List ─────────────────────────────────────────────────────────────
function SessionsList({ onOpen, onNew }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/v1/admin/training-sessions');
      setSessions(data.sessions ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Sesiones de Entrenamiento</h2>
          <p className="text-sm text-gray-500">Crear, ejecutar y registrar sesiones con jugadores</p>
        </div>
        <button
          onClick={onNew}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-lg transition"
        >
          <Plus className="w-4 h-4" /> Nueva sesión
        </button>
      </div>

      {loading && <div className="text-gray-500 text-sm">Cargando...</div>}

      {!loading && sessions.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay sesiones aún. Creá la primera.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sessions.map(s => {
          const plan = TRAINING_PLANS.find(p => p.id === s.planId);
          const c = PLAN_COLORS[s.planId] ?? PLAN_COLORS.tecnica;
          const Icon = PLAN_ICONS[s.planId] ?? Activity;
          const st = STATUS_STYLES[s.status] ?? STATUS_STYLES.DRAFT;
          return (
            <button
              key={s.id}
              onClick={() => onOpen(s.id)}
              className={`text-left bg-white rounded-2xl border ${c.border} p-5 hover:shadow-md transition`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.bg} ${c.text}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded ${st.color}`}>{st.label}</span>
              </div>
              <div className="text-xs font-bold text-gray-500 mb-1">PLAN {s.planCode} — {plan?.title}</div>
              <div className="font-semibold text-gray-900 mb-2">{s.title}</div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(s.date).toLocaleDateString('es-CL')}</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{s._count.attendees}</span>
                <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />{s._count.measurements} mediciones</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── New Session ──────────────────────────────────────────────────────────────
function NewSession({ onCreated, onCancel }) {
  const [planId, setPlanId] = useState('tecnica');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 16));
  const [players, setPlayers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    apiFetch('/api/v1/admin/players?limit=100').then(d => setPlayers(d.players ?? [])).catch(() => {});
  }, []);

  const plan = TRAINING_PLANS.find(p => p.id === planId);
  const c = PLAN_COLORS[planId];

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const submit = async () => {
    if (!title || selected.size === 0) {
      alert('Completa título y selecciona al menos 1 jugador');
      return;
    }
    setCreating(true);
    try {
      const data = await apiFetch('/api/v1/admin/training-sessions', {
        method: 'POST',
        body: JSON.stringify({
          planId,
          planCode: plan.code,
          title,
          date: new Date(date).toISOString(),
          playerIds: Array.from(selected),
        }),
      });
      onCreated(data.session.id);
    } catch (e) {
      alert('Error creando sesión');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={onCancel} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Cancelar
      </button>

      <h2 className="text-xl font-bold text-gray-900">Nueva sesión</h2>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-2 block">Plan de entrenamiento</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TRAINING_PLANS.map(p => {
              const active = planId === p.id;
              const pc = PLAN_COLORS[p.id];
              const Icon = PLAN_ICONS[p.id];
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlanId(p.id)}
                  className={`p-3 rounded-lg border-2 transition text-left ${active ? `${pc.border} ${pc.bg}` : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <Icon className={`w-4 h-4 mb-1 ${active ? pc.text : 'text-gray-400'}`} />
                  <div className={`text-xs font-bold ${active ? pc.text : 'text-gray-500'}`}>Plan {p.code}</div>
                  <div className="text-sm font-semibold text-gray-900">{p.title}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Título</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`Ej: ${plan?.title} — Grupo A`}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Fecha y hora</label>
            <input
              type="datetime-local"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-semibold text-gray-700">Jugadores ({selected.size} seleccionados)</label>
          <button
            type="button"
            onClick={() => setSelected(selected.size === players.length ? new Set() : new Set(players.map(p => p.id)))}
            className="text-xs text-emerald-600 hover:underline"
          >
            {selected.size === players.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
          </button>
        </div>
        {players.length === 0 && <div className="text-sm text-gray-500 text-center py-4">No hay jugadores cargados</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
          {players.map(p => {
            const active = selected.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 transition text-left ${active ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${active ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                  {active && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                  <div className="text-xs text-gray-500">{p.position} • {p.age} años • {p.city}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={submit}
          disabled={creating}
          className={`bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-lg transition ${c?.bg}`}
        >
          {creating ? 'Creando...' : 'Crear y empezar sesión'}
        </button>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 font-medium px-3 py-2.5">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// Parse time range "0:20-0:55" -> duration in minutes
function parseBlockDuration(time) {
  const [start, end] = time.split('-').map(t => {
    const [mm, ss] = t.split(':').map(Number);
    return mm * 60 + (ss || 0);
  });
  return Math.max(0, end - start); // minutes
}

// ─── Timer ────────────────────────────────────────────────────────────────────
// Supports chronometer (count-up) and countdown modes.
function Timer({ durationMinutes, blockKey }) {
  const [mode, setMode] = useState('up'); // 'up' | 'down'
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  // Reset when block changes
  useEffect(() => {
    setRunning(false);
    setSeconds(mode === 'down' ? (durationMinutes || 0) * 60 : 0);
  }, [blockKey, mode, durationMinutes]);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setSeconds(s => {
          if (mode === 'down') {
            if (s <= 0) { setRunning(false); return 0; }
            return s - 1;
          }
          return s + 1;
        });
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [running, mode]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  const isAlmostDone = mode === 'down' && seconds > 0 && seconds <= 30;
  const isDone = mode === 'down' && seconds === 0;

  return (
    <div className={`rounded-2xl p-5 flex items-center justify-between transition ${isDone ? 'bg-red-600' : isAlmostDone ? 'bg-amber-600' : 'bg-gray-900'} text-white`}>
      <div className="flex items-center gap-4">
        <Clock className="w-5 h-5 text-emerald-400" />
        <span className="text-3xl sm:text-4xl font-mono font-bold tabular-nums">{mm}:{ss}</span>
        <div className="flex bg-black/30 rounded-lg p-0.5">
          <button
            onClick={() => setMode('up')}
            className={`text-xs font-semibold px-2 py-1 rounded ${mode === 'up' ? 'bg-white text-gray-900' : 'text-white/70'}`}
          >
            Cronómetro
          </button>
          <button
            onClick={() => setMode('down')}
            className={`text-xs font-semibold px-2 py-1 rounded ${mode === 'down' ? 'bg-white text-gray-900' : 'text-white/70'}`}
            disabled={!durationMinutes}
          >
            Countdown {durationMinutes ? `(${durationMinutes}m)` : ''}
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => setRunning(!running)}
          className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg p-2.5 transition"
          aria-label={running ? 'Pausar' : 'Iniciar'}
        >
          {running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </button>
        <button
          onClick={() => { setRunning(false); setSeconds(mode === 'down' ? (durationMinutes || 0) * 60 : 0); }}
          className="bg-gray-700 hover:bg-gray-600 text-white rounded-lg p-2.5 transition"
          aria-label="Resetear"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// ─── Block Detail (rich view) ─────────────────────────────────────────────────
function BlockDetail({ block, colorClasses: c }) {
  const [activeExercise, setActiveExercise] = useState(0);
  const exercise = block.exercises?.[activeExercise];

  return (
    <div className="border-t border-gray-100 pt-4 space-y-4">
      {/* Top: image + header */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {block.image && (
          <div className="md:col-span-1">
            <img
              src={block.image}
              alt={block.name}
              className="w-full h-44 md:h-full object-cover rounded-lg"
              loading="lazy"
            />
          </div>
        )}
        <div className={block.image ? 'md:col-span-2' : 'md:col-span-3'}>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-xs font-mono font-bold text-gray-500">{block.time}</span>
            <span className="font-bold text-gray-900 text-xl">{block.name}</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">{block.content}</p>

          {block.objective && (
            <div className={`${c.bg} rounded-lg p-3 mb-2`}>
              <div className={`text-[10px] font-bold ${c.text} uppercase mb-1`}>Objetivo del bloque</div>
              <div className="text-sm text-gray-800">{block.objective}</div>
            </div>
          )}

          {block.equipment && block.equipment.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {block.equipment.map((e, i) => (
                <span key={i} className="text-[11px] font-medium bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Exercises (if any) */}
      {block.exercises && block.exercises.length > 0 && (
        <div>
          <div className="text-xs font-bold text-gray-500 uppercase mb-2">Ejercicios del bloque ({block.exercises.length})</div>
          <div className="flex gap-1 overflow-x-auto mb-3 pb-1">
            {block.exercises.map((ex, i) => (
              <button
                key={i}
                onClick={() => setActiveExercise(i)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs transition ${activeExercise === i ? `${c.bg} ${c.text} font-bold` : 'text-gray-500 hover:bg-gray-50 font-medium'}`}
              >
                <div>{i + 1}. {ex.name}</div>
                <div className="text-[10px] opacity-75 font-normal">{ex.duration}</div>
              </button>
            ))}
          </div>

          {exercise && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-bold text-gray-900">{exercise.name}</span>
                  <span className="text-xs text-gray-500">· {exercise.duration}</span>
                </div>
                <p className="text-sm text-gray-700">{exercise.description}</p>
              </div>

              {exercise.keyPoints && exercise.keyPoints.length > 0 && (
                <div>
                  <div className={`text-[10px] font-bold ${c.text} uppercase mb-1`}>Puntos clave</div>
                  <ul className="space-y-0.5">
                    {exercise.keyPoints.map((kp, i) => (
                      <li key={i} className="text-xs text-gray-700 flex gap-2">
                        <span className={c.text}>✓</span> {kp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Coach cues / mistakes / variations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {block.coachCues && block.coachCues.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-[10px] font-bold text-blue-700 uppercase mb-1">🗣️ Consignas del profe</div>
            <ul className="space-y-0.5">
              {block.coachCues.map((q, i) => (
                <li key={i} className="text-xs text-gray-700 italic">{q}</li>
              ))}
            </ul>
          </div>
        )}
        {block.commonMistakes && block.commonMistakes.length > 0 && (
          <div className="bg-red-50 rounded-lg p-3">
            <div className="text-[10px] font-bold text-red-700 uppercase mb-1">⚠️ Errores a evitar</div>
            <ul className="space-y-0.5">
              {block.commonMistakes.map((m, i) => (
                <li key={i} className="text-xs text-gray-700">{m}</li>
              ))}
            </ul>
          </div>
        )}
        {block.variations && (
          <div className="bg-emerald-50 rounded-lg p-3">
            <div className="text-[10px] font-bold text-emerald-700 uppercase mb-1">🎯 Adaptaciones</div>
            {block.variations.easier && (
              <div className="text-xs text-gray-700 mb-1">
                <span className="font-bold">Más fácil:</span> {block.variations.easier}
              </div>
            )}
            {block.variations.harder && (
              <div className="text-xs text-gray-700">
                <span className="font-bold">Más difícil:</span> {block.variations.harder}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Session Runner ───────────────────────────────────────────────────────────
function SessionRunner({ sessionId, onBack }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBlock, setActiveBlock] = useState(0);
  const [showMeasure, setShowMeasure] = useState(null); // playerId
  const saveTimeoutRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/api/v1/admin/training-sessions/${sessionId}`);
      setSession(data.session);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const plan = session && TRAINING_PLANS.find(p => p.id === session.planId);

  const updateAttendance = useCallback((playerId, patch) => {
    // Optimistic local update
    setSession(s => ({
      ...s,
      attendees: s.attendees.map(a =>
        a.playerId === playerId ? { ...a, ...patch } : a
      ),
    }));
    // Debounced save
    clearTimeout(saveTimeoutRef.current[playerId]);
    saveTimeoutRef.current[playerId] = setTimeout(() => {
      apiFetch(`/api/v1/admin/training-sessions/${sessionId}/attendance/${playerId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }).catch(() => {});
    }, 400);
  }, [sessionId]);

  const toggleBlockCompletion = (playerId, blockIdx) => {
    const att = session.attendees.find(a => a.playerId === playerId);
    const completions = { ...(att?.blockCompletions ?? {}) };
    completions[blockIdx] = !completions[blockIdx];
    updateAttendance(playerId, { blockCompletions: completions });
  };

  const setRPE = (playerId, rpe) => updateAttendance(playerId, { rpe });

  const markComplete = async () => {
    if (!window.confirm('¿Marcar la sesión como completada?')) return;
    await apiFetch(`/api/v1/admin/training-sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'COMPLETED' }),
    });
    load();
  };

  const startSession = async () => {
    await apiFetch(`/api/v1/admin/training-sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'IN_PROGRESS' }),
    });
    load();
  };

  if (loading || !session) return <div className="text-gray-500">Cargando sesión...</div>;

  const c = PLAN_COLORS[session.planId] ?? PLAN_COLORS.tecnica;
  const Icon = PLAN_ICONS[session.planId] ?? Activity;
  const st = STATUS_STYLES[session.status];
  const block = plan?.blocks?.[activeBlock];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex gap-2">
          {session.status === 'DRAFT' && (
            <button onClick={startSession} className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm">
              Empezar sesión
            </button>
          )}
          {session.status === 'IN_PROGRESS' && (
            <button onClick={markComplete} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-lg text-sm">
              Completar sesión
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className={`bg-white rounded-2xl border ${c.border} p-6`}>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${c.bg} ${c.text}`}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold ${c.text}`}>PLAN {session.planCode}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{session.title}</h2>
            <div className="flex gap-4 text-sm text-gray-500 mt-1">
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{new Date(session.date).toLocaleString('es-CL')}</span>
              <span className="flex items-center gap-1"><Users className="w-4 h-4" />{session.attendees.length} jugadores</span>
            </div>
          </div>
        </div>
      </div>

      {/* Timer (tied to active block for countdown) */}
      <Timer
        blockKey={activeBlock}
        durationMinutes={block ? parseBlockDuration(block.time) : 0}
      />

      {/* Block tabs */}
      {plan?.blocks && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex gap-1 overflow-x-auto mb-4 pb-1">
            {plan.blocks.map((b, i) => (
              <button
                key={i}
                onClick={() => setActiveBlock(i)}
                className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition ${activeBlock === i ? `${c.bg} ${c.text}` : 'text-gray-500 hover:bg-gray-50'}`}
              >
                <div className="font-bold">Bloque {i + 1}</div>
                <div className="text-[10px] opacity-75">{b.time}</div>
              </button>
            ))}
          </div>
          {block && <BlockDetail block={block} colorClasses={c} />}
        </div>
      )}

      {/* Player grid — checklist + RPE + measurements */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Jugadores — Bloque {activeBlock + 1}</h3>
          <span className="text-xs text-gray-500">Marcá a los que completaron la actividad</span>
        </div>
        <div className="divide-y divide-gray-100">
          {session.attendees.map(att => {
            const done = att.blockCompletions?.[activeBlock];
            const absent = att.attended === false;
            return (
              <div key={att.playerId} className={`px-5 py-3 flex items-center gap-4 ${absent ? 'opacity-40' : ''}`}>
                <button
                  onClick={() => !absent && toggleBlockCompletion(att.playerId, activeBlock)}
                  disabled={absent}
                  className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition ${done ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-emerald-400'} ${absent ? 'cursor-not-allowed' : ''}`}
                >
                  {done && <Check className="w-5 h-5 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{att.player.name}</div>
                  <div className="text-xs text-gray-500">{att.player.position} • {att.player.age} años</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateAttendance(att.playerId, { attended: absent })}
                    className={`text-[10px] font-bold uppercase px-2 py-1 rounded transition ${absent ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                  >
                    {absent ? 'Ausente' : 'Presente'}
                  </button>
                  <span className="text-xs text-gray-500 hidden sm:inline">RPE</span>
                  <select
                    value={att.rpe ?? ''}
                    onChange={e => setRPE(att.playerId, e.target.value ? parseInt(e.target.value) : null)}
                    disabled={absent}
                    className="border border-gray-200 rounded px-2 py-1 text-xs disabled:opacity-50"
                  >
                    <option value="">—</option>
                    {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <button
                    onClick={() => setShowMeasure(att.playerId)}
                    disabled={absent}
                    className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <TrendingUp className="w-3.5 h-3.5" /> Medir
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bloque-level summary: count done / total */}
      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-gray-400" />
        Progreso bloque {activeBlock + 1}: <span className="font-bold text-gray-900">
          {session.attendees.filter(a => a.blockCompletions?.[activeBlock]).length}
        </span> / {session.attendees.length} completaron
      </div>

      {showMeasure && (
        <MeasurementModal
          playerId={showMeasure}
          sessionId={sessionId}
          playerName={session.attendees.find(a => a.playerId === showMeasure)?.player?.name}
          onClose={() => setShowMeasure(null)}
        />
      )}
    </div>
  );
}

// ─── Measurement Modal ────────────────────────────────────────────────────────
function MeasurementModal({ playerId, sessionId, playerName, onClose }) {
  const [type, setType] = useState('SPRINT_20M');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const meta = MEASUREMENT_TYPES.find(m => m.key === type);

  useEffect(() => {
    apiFetch(`/api/v1/admin/players/${playerId}/measurements`)
      .then(d => setHistory(d.measurements ?? []))
      .catch(() => {});
  }, [playerId]);

  const submit = async () => {
    if (!value) return;
    setSaving(true);
    try {
      await apiFetch('/api/v1/admin/measurements', {
        method: 'POST',
        body: JSON.stringify({
          playerId,
          sessionId,
          type,
          value: parseFloat(value),
          unit: meta.unit,
          notes: notes || undefined,
        }),
      });
      // Reload history
      const d = await apiFetch(`/api/v1/admin/players/${playerId}/measurements`);
      setHistory(d.measurements ?? []);
      setValue('');
      setNotes('');
    } catch (e) {
      alert('Error guardando medición');
    } finally {
      setSaving(false);
    }
  };

  const typeHistory = history.filter(h => h.type === type);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Registrar medición</h3>
            <p className="text-sm text-gray-500">{playerName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 block">Tipo de medición</label>
            <select
              value={type}
              onChange={e => setType(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {['Físico','Técnico','Antropo.','Mental'].map(g => (
                <optgroup key={g} label={g}>
                  {MEASUREMENT_TYPES.filter(m => m.group === g).map(m => (
                    <option key={m.key} value={m.key}>{m.label} ({m.unit})</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Valor ({meta.unit})</label>
              <input
                type="number"
                step="0.01"
                value={value}
                onChange={e => setValue(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Notas (opcional)</label>
              <input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <button
            onClick={submit}
            disabled={saving || !value}
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg"
          >
            {saving ? 'Guardando...' : 'Registrar medición'}
          </button>

          {typeHistory.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h4 className="font-semibold text-sm text-gray-700 mb-3">Histórico — {meta.label}</h4>
              <MeasurementChart history={typeHistory} unit={meta.unit} />
              <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
                {typeHistory.slice(0, 10).map(h => (
                  <div key={h.id} className="flex justify-between text-xs text-gray-600 py-1 border-b border-gray-50">
                    <span>{new Date(h.measuredAt).toLocaleDateString('es-CL')}</span>
                    <span className="font-semibold text-gray-900">{h.value} {h.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Simple SVG line chart ────────────────────────────────────────────────────
function MeasurementChart({ history, unit }) {
  if (history.length < 2) {
    return <div className="text-xs text-gray-500 italic">Al menos 2 mediciones para ver la curva.</div>;
  }
  const sorted = [...history].sort((a, b) => new Date(a.measuredAt) - new Date(b.measuredAt));
  const vals = sorted.map(h => h.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 400, h = 80, pad = 10;
  const points = sorted.map((m, i) => {
    const x = pad + (i / (sorted.length - 1)) * (w - pad * 2);
    const y = h - pad - ((m.value - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20 bg-gray-50 rounded">
      <path d={path} fill="none" stroke="#10b981" strokeWidth="2" />
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#10b981" />
      ))}
      <text x={pad} y={h - 2} fontSize="9" fill="#9ca3af">{min.toFixed(1)} {unit}</text>
      <text x={w - pad - 40} y={h - 2} fontSize="9" fill="#9ca3af" textAnchor="end">{max.toFixed(1)} {unit}</text>
    </svg>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function TrainingSessionsTab() {
  const [view, setView] = useState({ screen: 'list' }); // list | new | runner
  const [currentId, setCurrentId] = useState(null);

  if (view.screen === 'new') {
    return <NewSession onCreated={(id) => { setCurrentId(id); setView({ screen: 'runner' }); }} onCancel={() => setView({ screen: 'list' })} />;
  }
  if (view.screen === 'runner') {
    return <SessionRunner sessionId={currentId} onBack={() => setView({ screen: 'list' })} />;
  }
  return (
    <SessionsList
      onOpen={(id) => { setCurrentId(id); setView({ screen: 'runner' }); }}
      onNew={() => setView({ screen: 'new' })}
    />
  );
}
